<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('receipt.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('toHomePage'), href: '/' },
          { text: $t('receipt.pageTitle'), href: '/' },
          {
            text: $t('create'),
            active: true
          }
        ]"
      />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <ts-loading-banner :loading="loading">
          <div class="row">
            <div class="col-md-12 ts-space-y-3">
              <div class="ts-p-2 ts-border ts-border-dashed ts-rounded-md">
                <div
                  class="
                   ts-py-1 ts-px-3 ts-bg-gray-200 ts-text-gray-600
                   ts-rounded-md ts-text-lg ts-font-semibold"
                  style="width: fit-content;"
                >
                  {{ $t('receipt.invoice') }}
                </div>
                <a-input-search
                  :placeholder="$t('receipt.searchInvoice')"
                  @search="search => getInvoiceList({ search })"
                  class="ts-mt-2"
                />
                <div class="ts-overflow-auto" style="max-height: 300px;">
                  <table id="list" class="table mt-3">
                    <thead>
                      <tr>
                        <th>{{ $t('receipt.invoiceNumber') }}</th>
                        <th>{{ $t('receipt.studentId') }}</th>
                        <th>{{ $t('receipt.studentName') }}</th>
                        <th>{{ $t('receipt.sex') }}</th>
                        <th>{{ $t('receipt.nextPaymentDate') }}</th>
                        <th>{{ $t('receipt.amount') }}</th>
                        <th>{{ $t('receipt.amountDue') }}</th>
                        <th class="ts-w-10">{{ $t('actions') }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(resource, index) in resourcesExcludeSelected"
                        :key="index"
                      >
                        <td>{{ resource.inv_number }}</td>
                        <td>
                          {{
                            is_external
                              ? resource.ext_card_number
                              : resource.card_number
                          }}
                        </td>
                        <td>{{ resource.full_name_en }}</td>
                        <td>{{ resource.gender }}</td>
                        <td>{{ resource.next_payment_date }}</td>
                        <td>{{ resource.inv_amount }}</td>
                        <td>{{ resource.amount_due }}</td>
                        <td class="text-center">
                          <a
                            href="#"
                            class="ts-bg-blue-100 ts-px-2 ts-py-1 ts-rounded-full ts-text-blue-500"
                            v-tooltip="$t('receipt.pay')"
                            @click.prevent="makePayment(resource)"
                          >
                            <i class="fas fa-wallet"></i>
                          </a>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div class="ts-p-2">
                <div
                  class="
                   ts-py-1 ts-px-3 ts-bg-gray-200 ts-text-gray-600
                   ts-rounded-md ts-text-lg ts-font-semibold"
                  style="width: fit-content;"
                >
                  {{ $t('receipt.payment') }}
                </div>
                <div class="ts-mt-4">
                  <div class="row">
                    <div class="col-md-6 ">
                      <div
                        class="ts-border ts-border-dashed ts-rounded-md ts-p-2"
                      >
                        <div class="row">
                          <div class="col-md-6">
                            <label class="ts-mb-0 required">{{
                              $t('receipt.paymentDate')
                            }}</label>
                            <a-date-picker
                              v-model="model.payment_date"
                              format="DD-MM-YYYY"
                              valueFormat="DD-MM-YYYY"
                              :allowClear="false"
                              style="width: 100%;"
                              class="mb-2"
                            />
                            <div
                              class="validate"
                              v-if="errors.has('payment_date')"
                            >
                              {{ errors.first('payment_date') }}
                            </div>
                          </div>
                          <div class="col-md-6">
                            <label class=" ts-mb-0 required">{{
                              $t('receipt.invoiceNumber')
                            }}</label>
                            <input
                              v-model="model.invoice_number"
                              type="text"
                              class="form-control mb-2"
                              readonly
                            />
                            <div
                              class="validate"
                              v-if="errors.has('invoice_number')"
                            >
                              {{ errors.first('invoice_number') }}
                            </div>
                          </div>
                          <div class="col-md-6">
                            <label class=" ts-mb-0 required">{{
                              $t('receipt.studentName')
                            }}</label>
                            <input
                              v-model="model.student_name"
                              type="text"
                              class="form-control mb-2"
                              readonly
                            />
                            <div
                              class="validate"
                              v-if="errors.has('student_name')"
                            >
                              {{ errors.first('student_name') }}
                            </div>
                          </div>
                          <div class="col-md-6">
                            <label class="ts-mb-0">{{
                              $t('receipt.pendingAmount')
                            }}</label>
                            <input
                              type="text"
                              class="form-control mb-2"
                              readonly
                              v-model.number="model.inv_amount"
                            />
                            <div
                              class="validate"
                              v-if="errors.has('inv_amount')"
                            >
                              {{ errors.first('inv_amount') }}
                            </div>
                          </div>
                          <div class="col-md-6">
                            <label class="ts-mb-0">{{
                              $t('receipt.amountDue')
                            }}</label>
                            <input
                              type="text"
                              class="form-control"
                              v-model.number="model.amount_due"
                              readonly
                            />
                            <div
                              class="validate"
                              v-if="errors.has('amount_due')"
                            >
                              {{ errors.first('amount_due') }}
                            </div>
                          </div>
                          <div class="col-md-6">
                            <label
                              class="ts-mb-0"
                              :class="{ required: model.amount_due > 0 }"
                              >{{ $t('receipt.nextPaymentDate') }}</label
                            >
                            <a-date-picker
                              :disabled="disableNextPayment"
                              format="DD-MM-YYYY"
                              valueFormat="DD-MM-YYYY"
                              style="width: 100%; height: 35px"
                              v-model="model.next_payment_date"
                            />
                            <div
                              class="validate"
                              v-if="errors.has('next_payment_date')"
                            >
                              {{ errors.first('next_payment_date') }}
                            </div>
                          </div>
                        </div>
                        <div class="row">
                          <div class="col-md-12">
                            <label class="ts-mb-0">{{
                              $t('receipt.remark')
                            }}</label>
                            <textarea
                              v-model="model.comment"
                              class="form-control"
                              rows="3"
                            ></textarea>
                            <div class="validate" v-if="errors.has('comment')">
                              {{ errors.first('comment') }}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="col-md-6">
                      <div
                        class="ts-border ts-border-dashed ts-rounded-md ts-p-2"
                        style="min-height: 185px;"
                      >
                        <p
                          class="ts-bg-blue-100 ts-text-blue-500 ts-px-2 ts-py-1 ts-rounded-md"
                          style="width: fit-content;"
                        >
                          {{ $t('receipt.methods') }}
                        </p>
                        <div class="ts-p-2 ts-mt-2 ts-flex ts-space-x-2">
                          <div class="ts-space-y-2 ts-w-full">
                            <div
                              v-for="(payment, id) in model.received_payment"
                              class="ts-border ts-border-dashed ts-px-1 ts-rounded"
                            >
                              <div
                                class="ts-font-semibold ts-py-2 ts-flex ts-items-center ts-justify-between ts-space-x-2"
                              >
                                <div
                                  class="ts-flex ts-items-center ts-space-x-2"
                                >
                                  <p
                                    class="ts-bg-blue-100 ts-text-blue-500 ts-px-2 ts-rounded required"
                                  >
                                    {{ findPaymentNameById(id) }}
                                    <span class="ts-text-red-600">*</span>
                                  </p>
                                  <div class="ts-whitespace-no-wrap">
                                    <a-checkbox
                                      v-for="(c, index) in currency"
                                      :key="index"
                                      :checked="
                                        currencyCheck(id, c.currency_id)
                                      "
                                      @change="
                                        e =>
                                          onCurrencyChange(e, c, {
                                            id,
                                            payment
                                          })
                                      "
                                    >
                                      {{ c.currency_code }}
                                    </a-checkbox>
                                  </div>
                                </div>
                                <button
                                  @click="removePaymentMethod(id)"
                                  class="ts-bg-red-100 ts-h-6 ts-w-6 ts-flex ts-items-center ts-justify-center ts-rounded-full hover:ts-bg-red-500 hover:ts-text-white ts-duration-150 focus:ts-outline-none"
                                >
                                  <i class="fa fa-times"></i>
                                </button>
                              </div>
                              <div class="ts-pl-3">
                                <div
                                  v-if="parseInt(id) !== 1"
                                  class="ts-grid ts-grid-cols-2 ts-gap-2"
                                >
                                  <div>
                                    <a-select
                                      v-model="payment.bank_id"
                                      :options="bank"
                                      :placeholder="$t('invoice.selecteBank')"
                                      class="ts-w-full"
                                      @change="
                                        () => (payment.account_id = undefined)
                                      "
                                    >
                                    </a-select>
                                    <div
                                      v-if="
                                        errors.has(
                                          `received_payment.${id}.bank_id`
                                        )
                                      "
                                      class="ts-text-red-500"
                                    >
                                      {{
                                        errors.first(
                                          `received_payment.${id}.bank_id`
                                        )
                                      }}
                                    </div>
                                  </div>
                                  <div>
                                    <a-select
                                      v-model="payment.account_id"
                                      :options="accountById(payment.bank_id)"
                                      :placeholder="
                                        $t('invoice.selecteAccount')
                                      "
                                      class="ts-w-full"
                                    ></a-select>
                                    <div
                                      v-if="
                                        errors.has(
                                          `received_payment.${id}.account_id`
                                        )
                                      "
                                      class="ts-text-red-500"
                                    >
                                      {{
                                        errors.first(
                                          `received_payment.${id}.account_id`
                                        )
                                      }}
                                    </div>
                                  </div>
                                </div>
                                <div>
                                  <div
                                    v-for="(p, pindex) in payment.payments"
                                    class="ts-py-2 ts-flex ts-items-center ts-space-x-2"
                                  >
                                    <p>
                                      {{
                                        currencyNameById(p.currency_original_id)
                                      }}
                                    </p>
                                    <div class="ts-space-y-1 ts-w-full">
                                      <input
                                        v-model.number="p.amount_original"
                                        type="number"
                                        step="any"
                                        class="ts-border ts-w-full ts-px-3 ts-py-1 focus:ts-outline-none ts-rounded"
                                        :ref="
                                          `input_${id}_${p.currency_original_id}`
                                        "
                                        @input="paymentReceiveInput"
                                      />
                                      <div
                                        v-if="
                                          errors.has(
                                            `received_payment.${id}.payments.${pindex}.amount_original`
                                          )
                                        "
                                        class="ts-text-red-500"
                                      >
                                        {{
                                          errors.first(
                                            `received_payment.${id}.payments.${pindex}.amount_original`
                                          )
                                        }}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <a-dropdown placement="bottomCenter">
                              <button
                                class="ts-bg-blue-200 ts-text-blue-500 ts-rounded-md ts-px-3 ts-py-2 ts-w-full hover:ts-bg-blue-300 hover:ts-text-white ts-duration-150 focus:ts-outline-none"
                              >
                                {{ $t('invoice.morePaymentMethod') }}
                              </button>
                              <a-menu slot="overlay">
                                <a-menu-item
                                  v-for="(p, index) in paymentMethodsList"
                                  :key="index"
                                  @click="addPaymentMethod(p.id)"
                                  >{{ p.label }}</a-menu-item
                                >
                              </a-menu>
                            </a-dropdown>
                            <div
                              v-if="errors.has('received_payment')"
                              class="ts-text-red-500"
                            >
                              {{ errors.first('received_payment') }}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="row ts-mt-2">
                    <div class="col-md-12 ts-flex ts-justify-end">
                      <ts-button class="mr-2" @click.prevent="cancelPayment">{{
                        $t('cancel')
                      }}</ts-button>
                      <ts-button color="primary" @click.prevent="payReceipt">{{
                        $t('save')
                      }}</ts-button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ts-loading-banner>
      </ts-card-wrapper>
    </ts-card>

    <a-modal
      width="50%"
      centered
      v-model="previewPdf"
      :title="$t('pdf')"
      :maskClosable="false"
      :okText="$t('print')"
      :cancelText="$t('cancel')"
      :cancelButtonProps="{ class: 'btn ts-whitespace-no-wrap btn-default' }"
      :footer="null"
      style="top: 3vh;"
      :zIndex="2000"
      @cancel="clearInput"
    >
      <div id="container">
        <iframe :src="src"></iframe>
      </div>
    </a-modal>
  </div>
</template>
<script>
import { Errors } from 'form-backend-validation'
import { debounce, round, sumBy } from 'lodash'
import moment from 'moment'
import { mapActions } from 'vuex'
import FormComponentV1 from './comments/form_v1.vue'
import PreviewPdfComponent from './comments/pdf-preview'

export default {
  name: 'receiptCreate',
  components: {
    FormComponentV1,
    PreviewPdfComponent
  },
  data() {
    return {
      waiting: false,
      loading: false,
      search: null,
      previewPdf: false,
      src: null,
      resources: [],
      banks: [],
      errors: new Errors(),
      model: {
        inv_id: null,
        invoice_number: null,
        comment: null,
        inv_amount: null,
        amount_paid: null,
        amount_due: 0,
        payment_date: moment().format('DD-MM-YYYY'),
        next_payment_date: null,
        is_return: 1, // 1 is return
        student_name: '',
        received_payment: {}
      },
      paymentMethods: [],
      bank: [],
      currency: [],
      exchangeRate: []
    }
  },
  mounted() {
    this.fetchResource()
    this.getDefaultCreditDay()
  },
  computed: {
    disableNextPayment() {
      return !this.model.amount_due > 0
    },
    is_external() {
      return this.$store.state.authUser.is_external
    },
    resourcesExcludeSelected() {
      if (!this.model.inv_id) return this.resources

      return this.resources.filter(r => r.inv_id !== this.model.inv_id)
    },
    withDefaultCurrency: vm => value => {
      const defCurrency = vm.defaultCurrecy
      if (defCurrency) {
        return `${defCurrency.currency_code} ${value.toLocaleString('en-US')}`
      }
      return value
    },
    defaultCurrecy() {
      return this.currency.find(c => c.is_default)
    },
    currencyNameById: vm => id => {
      const curr = vm.currency.find(c => c.currency_id === id)
      if (curr) {
        return curr.currency_code
      }
      return undefined
    },
    paymentMethodsList() {
      return this.paymentMethods.filter(
        p =>
          !Object.keys(this.model.received_payment)
            .map(Number)
            .includes(p.id)
      )
    },
    findPaymentNameById: vm => id => {
      const findPayment = vm.paymentMethods.find(p => p.id == id)
      if (findPayment) {
        return findPayment.label
      }
      return ''
    },
    currencyCheck: vm => (id, currencyId) => {
      const itemPayment = vm.model.received_payment[id]
      if (itemPayment.payments) {
        return (
          itemPayment.payments.find(
            ip => ip.currency_original_id == currencyId
          ) !== undefined
        )
      }
      return false
    },
    findExchangeRateById: vm => id => {
      return vm.exchangeRate.find(ex => ex.target_currency_id === id)
    },
    accountById: vm => id => {
      const findBank = vm.bank.find(b => b.value === id)
      if (findBank) {
        return findBank.accounts.map(el => ({
          value: el.account_id,
          label: `${el.account_name}`
        }))
      }
    }
  },
  methods: {
    ...mapActions('receipt', [
      'fetchInvoice',
      'store',
      'getDefaultCreditDay',
      'formViewModel'
    ]),
    async fetchResource() {
      this.loading = true
      const responseFormView = await this.formViewModel({
        fnName: 'bank,paymentMethod,currency,exchangeRate'
      })
      const invoices = await this.fetchInvoice()

      const { data } = responseFormView

      this.resources = invoices.data

      this.currency = data.currency
      this.paymentMethods = data.paymentMethod
      this.exchangeRate = data.exchangeRate

      this.bank = data.bank.map(el => ({
        value: el.bank_id,
        label: el.bank_name,
        accounts: el.bank_accounts
      }))

      this.loading = false
    },
    getInvoiceList(attributes = {}) {
      this.loading = true
      this.fetchInvoice(attributes)
        .then(async response => {
          this.resources = response.data
        })
        .finally(() => {
          this.loading = false
        })
    },
    makePayment(record) {
      this.errors = new Errors()
      this.model.inv_id = record.inv_id
      this.model.invoice_number = record.inv_number
      this.model.student_name = record.full_name_en
      this.model.inv_amount = record.amount_due // set amount due the previous invoice to pay again
      this.model.payment_date = moment().format('DD-MM-YYYY')
      this.model.next_payment_date = null
      this.model.comment = null
    },
    payReceipt() {
      this.loading = true
      this.src = null
      this.store(this.model)
        .then(response => {
          this.$toasted.success(response.message)
          this.clearInput()
          this.getInvoiceList()
          this.previewPdf = true
          this.src = response.url
        })
        .catch(error => {
          this.errors = new Errors(error.errors)
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.loading = false
        })
    },
    cancelPayment() {
      this.$router.push({ name: 'receipt' })
    },
    clearInput() {
      this.model.inv_id = null
      this.model.invoice_number = null
      this.model.inv_amount = null
      this.model.amount_paid = null
      this.model.amount_due = 0
      this.model.payment_date = moment().format('DD-MM-YYYY')
      this.model.next_payment_date = null
      this.model.comment = null
      this.model.student_name = null
      this.model.is_return = 1 // 1 is return
      // this.model.payment_type = 2 // 2 cash
      this.model.received_payment = {}
      this.src = null
      this.previewPdf = false
    },
    paymentReceiveInput() {
      const paymentReceive = Object.values(this.model.received_payment)
      const payment = paymentReceive.flatMap(el => el.payments)
      if (payment) {
        const amount = sumBy(payment, o => o.amount_original * o.rate_to_base)
        this.model.amount_paid = amount
        this.model.amount_due = round(this.model.inv_amount - (amount || 0), 2)
      } else {
        this.model.amount_paid = 0
        this.model.amount_due = this.model.inv_amount
      }
    },
    addPaymentMethod(id) {
      // Input validation
      if (!id) {
        throw new Error('Payment method ID is required')
      }

      if (!this.defaultCurrecy) {
        this.$toasted.error('Please set default currency first!')
        return
      }

      // Initialize received_payment if it doesn't exist
      if (!this.model.received_payment) {
        this.model.received_payment = {}
      }

      // Use spread operator for cleaner object assignment
      this.model.received_payment = {
        ...this.model.received_payment,
        [id]: {
          bank_id: undefined,
          account_id: undefined,
          payments: [
            {
              amount_original: undefined,
              currency_original_id: this.defaultCurrecy.currency_id,
              base_currency: this.defaultCurrecy.currency_id,
              rate_to_base: 1
            }
          ]
        }
      }
      this.focusInputElement(id, this.defaultCurrecy.currency_id)
    },
    removePaymentMethod(id) {
      if (this.$delete) {
        this.$delete(this.model.received_payment, id)
      } else {
        // Fallback: Replace entire object to trigger reactivity
        const { [id]: removed, ...remaining } = this.model.received_payment
        this.model.received_payment = remaining
      }

      this.paymentReceiveInput()
    },
    onCurrencyChange(event, currency, { id, payment }) {
      const isChecked = event.target.checked
      const existing = payment.payments.find(
        p => p.currency_original_id === currency.currency_id
      )
      const exchange = this.findExchangeRateById(currency.currency_id)
      if (
        currency.currency_id !== this.defaultCurrecy.currency_id &&
        exchange === undefined
      ) {
        this.$toasted.error(
          `Please set up exchange rate for ${currency.currency_code} first!`
        )
        return
      }
      if (isChecked) {
        if (!existing) {
          payment.payments.push({
            amount_original: undefined,
            currency_original_id: currency.currency_id,
            base_currency: this.defaultCurrecy.currency_id,
            rate_to_base:
              currency.currency_id !== this.defaultCurrecy.currency_id
                ? exchange.rate_to_default
                : 1
          })
          this.focusInputElement(id, currency.currency_id)
        }
        return
      }

      if (existing) {
        payment.payments.splice(payment.payments.indexOf(existing), 1)
        this.paymentReceiveInput()
      }
    },
    focusInputElement(id, currency_id) {
      this.$nextTick(() => {
        const refKey = `input_${id}_${currency_id}`
        const inputElement = this.$refs[refKey]
        if (inputElement) {
          if (Array.isArray(inputElement)) {
            inputElement[0].focus()
          } else {
            inputElement.focus()
          }
        }
      })
    }
  },
  watch: {
    search: debounce(function(search) {
      this.getInvoiceList({ search: search })
    }, 500),
    is_external: debounce(function(val) {
      this.getInvoiceList({ search: this.search })
    }, 500)
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('receipt/RESET_STATE')
    next()
  }
}
</script>

<style lang="css" scoped>
label {
  font-size: 12px !important;
}

#list>thead>tr>th {
  /* padding: 5px 4px; */
  background: #f2f3f4;
  text-transform: uppercase;
}

#list>tbody>tr> {
  border-bottom: 1px solid gray;
}

#list>tbody>tr>td {
  /* padding: 5px 4px; */
  border-top: 1px solid rgb(223, 223, 223);
  font-size: 12px !important;
}

#container {
  width: 100%;
  height: 70vh;
  background: black;
  text-align: center;
}

#container iframe {
  width: 100%;
  height: 70vh;
  border: 2px solid #acacac;
}

#container i {
  margin-top: 32vh;
}
</style>
